import { getDevice } from 'dss-device';
import { deepGet, deepGetOfType } from 'bam-deep-get';
import uuidv4 from 'uuid/v4';

/**
 * Class listens for trackable events and fires custom event to allow for easy use by integrated
 *  Analytics platforms
 */
export default class AnalyticsManager {
    constructor(skdPayload) {
        const { UserActivity } = skdPayload;
        this.triggerEvent = this.triggerEvent.bind(this);
        this.deviceData = {};
        this.userData = {};
        this.userActivityApi = UserActivity.UserActivityApi;
        this.glimpseEvents = UserActivity.GlimpseEvents;
        this.glimpseEvent = UserActivity.GlimpseEvent;

        this.initContextData(skdPayload.sessionInfo);
        this.bindPageLoad();
        this.bindElementEvents();
    }

    /**
     * @description Getter returns the gloabl context data
     * @returns {object}
     */
    get contextData() {
        return {
            ...this.deviceData,
            ...this.userData,
            ...this.updateDynamicData()
        };
    }

    get sessionInfo() {
        return this._sessionInfo;
    }

    get appVersion() {
        const { env } = window.server_path;
        const subdomain = window.location.host.split('.')[0];
        const envString = (subdomain === 'www') ? '' : `__${env}`;

        return `${window.server_path.appVersion}${envString}`;
    }

    /**
     * @description Initialize the static context data
     * @param {object} sessionInfo - The current session info from the bam-browser-sdk-sdkSession-util module
     */
    initContextData(sessionInfo) {
        const device = getDevice();
        const {
            s_account: adobeRsid,
            navigator,
            server_path: serverPath
        } = window;
        const { browser, browserVersion, formFactor, platform, platformVersion, userAgent } = device;
        const deviceStr = (formFactor === 'phone') || (formFactor === 'tablet') ? 'Mobile Web' : 'Web';

        this._sessionInfo = sessionInfo;

        this.deviceData = {
            browserType: browser,
            browserVersion,
            connectionType: deepGet(navigator, 'connection.type', 'unknown'),
            device: deviceStr,
            deviceType: formFactor,
            osName: platform,
            osVersion: platformVersion,
            partner: deepGetOfType(window, 'server_path.analytics.partner', String),
            platform: 'Web',
            platformType: 'Web',
            userAgent,
            webAppName: deepGetOfType(window, 'server_path.analytics.adobe.webAppName', String)
        };

        this.userData = {
            adobeRsid,
            adobeRsidName: serverPath.analytics.adobe.rsidname,
            deviceLanguage: navigator.language.split('-')[0],
            deviceLocale: navigator.language,
            dssDeviceId: (sessionInfo) ? sessionInfo.device.id : '',
            dssSessionId: (sessionInfo) ? sessionInfo.id : ''
        };
    }

    /**
     * @description Some analyics data changes per event tracked.
     * @return {Object} Dynamic Data
     */
    updateDynamicData() {
        const { s, visitor } = window;
        const { screenOrientation } = getDevice();
        const cid = s ? s.Util.getQueryParam('cid') : '';
        const partnerId = s ? s.Util.getQueryParam('partnerId') : '';
        const testId = s ? s.Util.getQueryParam('testId') : '';
        const dssAccountId = deepGet(this._sessionInfo, 'account.id', null);
        const dssProfileId = deepGet(this._sessionInfo, 'profile.id', null);
        const enableCMP = deepGetOfType(window, 'dssMlpConfigs.regionConfig.featureConfig.enableCMP', Boolean);
        let consentStatus;

        const consentGroups = (window.OptanonActiveGroups) ?
            window.OptanonActiveGroups.trim().split(',').filter(g => g) : [];

        if (!consentGroups.length) {
            consentStatus = (enableCMP) ? '1' : '1,2,3,4';
        } else {
            consentStatus = consentGroups.join(',');
        }

        let optimizelyContextData;

        if (window.optimizelyData) {
            optimizelyContextData = {
                optimizelyCampaignId: deepGet(window.optimizelyData, 'data.decision.campaignId'),
                optimizelyExperimentId: deepGet(window.optimizelyData, 'data.decision.experimentId'),
                optimizelyVariationId: deepGet(window.optimizelyData, 'data.decision.variationId')
            };
        }

        return {
            adobeSdkVersion: s ? s.version : '',
            appVersion: this.appVersion,
            cid,
            dssAccountId,
            dssProfileId,
            loggedIn: dssAccountId ? 'true' : 'false',
            mid: (visitor) ? visitor.getMarketingCloudVisitorID() : '',
            previousUrl: document.referrer,
            consentStatus,
            partnerId,
            screenOrientation,
            testId,
            timestamp: new Date().toISOString(),
            userVisitorState: s ? s.getNewRepeat() : '',
            ...optimizelyContextData
        };
    }

    /**
     * @description Find all elements that are to be tracked and listen for the specified event. Once that event is triggered
     * fire a custom event.
     */
    bindElementEvents() {
        document.querySelectorAll('[data-analytics]').forEach(el => {
            const captureEvent = el.getAttribute('data-analytics') || 'click';

            el.addEventListener(captureEvent, this.triggerEvent);
        });
    }

    /**
     * @description Listen for the page load event
     */
    bindPageLoad() {
        document.body.addEventListener('load', this.triggerEvent);
    }

    /**
     * @description Fire a custom event with the string syntax of 'analtyics_$type'
     * @example analytics_click, analytics_load, analytics_change, etc
     * @param {Event} e - Event Object
     */
    triggerEvent(e) {
        let customEvent;

        //IE 11 has CustomEvent Obj but doesn't like the constructor for IE reasons
        try {
            customEvent = new CustomEvent(`analytics_${e.type}`, {
                bubbles: true,
                detail: e.detail
            });
        } catch (err) {
            customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent(`analytics_${e.type}`, true, false, e.detail);
        }

        e.currentTarget.dispatchEvent(customEvent);
    }

    /** @description Fire the page load event */
    firePageLoad() {
        this.triggerEvent({ type: 'load', currentTarget: document.body });
    }

    /** @description Fire a special event
    * @param {Object} eventData Data to send thru to the analytics event
    * @param {String} eventData.action Special action being tracked
    */
    fireAction(eventData) {
        this.triggerEvent({ type: 'event', currentTarget: document.body, detail: {
            ...eventData
        } });
    }


    /**
     * @description Get the consentStatus based upon CMP approvals or defaults for the region.
     * @returns {Array} User's consent status
     */
    getConsentState() {
        const enableCMP = deepGetOfType(window, 'dssMlpConfigs.regionConfig.featureConfig.enableCMP', Boolean);

        let consentGroups = (window.OptanonActiveGroups) ? window.OptanonActiveGroups.trim().split(',').filter(g => g) : [];
        const categoryStrings = ['essential', 'performance_and_analytics', 'functional', 'targeting_and_advertising'];

        if (!consentGroups.length) {
            consentGroups = (enableCMP) ? ['1'] : ['1', '2', '3', '4'];
        }

        return consentGroups.map(index => {
            return categoryStrings[index - 1];
        });
    }

    /** @description Handle Glimpse Tracking Events **/
    fireGlimpseEvent(glimpseEvent) {
        const {
            appLaunched
        } = this.glimpseEvents;

        const glimpseTimeStamp = new Date();
        glimpseTimeStamp.toISOString();

        const glimpseV2Persisted = JSON.parse(sessionStorage.getItem('dss-glimpseV2-persist')) || {};
        const { activitySessionId } = glimpseV2Persisted;

        if (!activitySessionId) {
            const glimpseV2Init = {
                activitySessionId: uuidv4()
            };
            sessionStorage.setItem('dss-glimpseV2-persist', JSON.stringify(glimpseV2Init));
        }

        const correlationId = JSON.parse(sessionStorage.getItem('correlationId')) || uuidv4();
        sessionStorage.setItem('correlationId', JSON.stringify(correlationId));

        const glimpseTrackingParameters = {
            activitySessionId,
            adobeId: this.deviceData.mid,
            appName: deepGetOfType(window, 'server_path.analytics.adobe.webAppName', String),
            appVersion: this.appVersion,
            consentFallback: (!window.Optanon),
            consentStates: this.getConsentState(),
            correlationId,
            kochavaAppId: 'id_not_available',
            partner: this.deviceData.partner,
            platform: this.deviceData.platform,
            timestamp: glimpseTimeStamp
        };

        let event;
        let additionalParameters;

        switch (glimpseEvent) {
            case 'consentGranted':
                // eslint-disable-next-line new-cap
                event = new this.glimpseEvent('urn:dss:event:fed:lifecycle:consent:granted');
                additionalParameters = {
                    action: 'Click'
                };
                break;
        }

        if (event) {
            this.userActivityApi.setTrackingParameters(glimpseTrackingParameters);
            this.userActivityApi.trackEvent(event, additionalParameters);
        }

    }
}
